/**
 * The fleet page's day (Sonar-experience 112): what each van is doing on one date.
 *
 * Both the input schema AND the row shapes live here, which is a departure from the
 * leaf modules that ship only inputs and let tRPC infer the rest. The reason is 107's
 * `DueRow`: the web could not import a type out of `apps/api`, so it RESTATED the row
 * in the component, and a restated shape is one that drifts the first time a field is
 * added on one side. `MaintenanceCard` already lives in shared for exactly this reason
 * (review 106 forbade inventing the partition per surface) — the fleet day is the same
 * argument at row scale. One definition, imported by the service that builds it and the
 * page that draws it.
 */
import { z } from 'zod';
import { isoDate } from './asset';
import type { AssetState } from './asset';

/**
 * Which day, as the SHOP sees it.
 *
 * A calendar date, not an instant, and that is load-bearing. "Today" on the fleet page
 * is the shop's today — the service resolves it against the org's timezone the way
 * `listMaintenanceDue` does. A dispatcher opening the page from a hotel in another zone
 * must see the shop's day, not their own, and an instant would carry their zone in.
 * Absent means "the shop's today"; the browser never gets to decide what today is.
 */
export const fleetDayInput = z.object({ date: isoDate.optional() }).strict();
export type FleetDayInput = z.infer<typeof fleetDayInput>;

/** One placed call on a van's day. */
export interface FleetStop {
  visitId: string;
  jobId: string;
  /** `jobs.job_number` — an integer counter per org, not a formatted string. */
  jobNumber: number;
  title: string;
  status: string;
  customerName: string;
  propertyLabel: string | null;
  address: string | null;
  latitude: number;
  longitude: number;
  /** Never null here — a visit with no scheduled start is not on any day. */
  scheduledStartAt: Date;
  scheduledEndAt: Date | null;
  /** 'exact' | 'am' | 'pm' | 'all_day' — an arrival window is not a promise of a time. */
  window: string;
}

/**
 * A call that belongs to this van's day but cannot be drawn, with the reason in words.
 *
 * ⚠️ Carried per-vehicle rather than dropped, because dropping it is how a map lies by
 * omission: the van shows four pins, the tech drives five jobs, and nothing on the
 * screen accounts for the difference. `dayMap` already established the shape and the
 * three reasons; this reuses them verbatim.
 */
export interface FleetUnplaced {
  visitId: string;
  jobNumber: number;
  customerName: string;
  why: string;
}

/** One van, and what it is doing. */
export interface FleetVehicle {
  assetId: string;
  name: string;
  plate: string | null;
  /** "Transit 250" — make and model joined, or null when neither is recorded. */
  makeModel: string | null;
  status: AssetState;
  /**
   * The yard the day starts and ends at — the black `S` pin. Null when the van has no
   * home base recorded, or has one that has not been geocoded yet. A null base is not
   * an error: the route is then drawn between the stops alone.
   */
  base: { latitude: number; longitude: number } | null;
  /**
   * Who runs it on this date, from the assignments live on that date.
   *
   * ⚠️ PLURAL, and not for symmetry. `asset_assignments` carries no uniqueness over
   * (asset, date range) — the app-layer guard in `setAssignment` is "one active
   * VEHICLE per tech", which says nothing about two techs sharing one van. That is a
   * real arrangement, so a singular `driver` here would have had to pick one of them
   * and silently drop the other, along with their half of the van's day. Empty when
   * nobody is assigned.
   */
  drivers: { userId: string; name: string }[];
  /**
   * Ordered by scheduled start. Empty is the honest "No route planned" state.
   *
   * The union over `drivers`, deduplicated by visit: two techs sent to one job in one
   * van is one stop, not two.
   */
  stops: FleetStop[];
  unplaced: FleetUnplaced[];
  /**
   * Booked minutes on this van's day: the sum of `scheduledEndAt − scheduledStartAt`
   * over its stops.
   *
   * ⚠️ Derived on the SERVER, and not because a duration needs a calendar — it does not;
   * subtracting two instants is zone-free. It is here because `apps/web/src/v2/fleet` is
   * swept for `new Date(` (107), and that sweep exists so no date arithmetic can drift
   * into the browser by habit. One rule, no exception carved for the easy case.
   */
  bookedMinutes: number;
  /**
   * How many of this van's stops carry no end time.
   *
   * ⭐ Shipped beside the total rather than folded into it. Both scheduled columns are
   * nullable, so an open-ended call contributes zero — silently, and always downward. A
   * total with no denominator beside it reads as complete, and a shop plans against it.
   */
  stopsWithoutEnd: number;
}

/**
 * The whole day.
 *
 * ⭐ `unassigned` is not an afterthought — it is what stops this page being a REGRESSION
 * on the Map page it retires (D5). `/map` drew every scheduled visit in the shop. A
 * fleet page keyed on vehicles draws only the ones a van is assigned to, so a visit
 * given to a tech with no van, or to nobody at all, would silently vanish from the only
 * map the office has. It goes in its own group instead, and the page says so.
 */
export interface FleetDay {
  /** The shop's date this answers about, ISO `YYYY-MM-DD`. */
  date: string;
  vehicles: FleetVehicle[];
  unassigned: {
    stops: FleetStop[];
    unplaced: FleetUnplaced[];
    bookedMinutes: number;
    stopsWithoutEnd: number;
  };
  /**
   * The whole day's booked minutes — every van plus the unassigned group.
   *
   * ⚠️ NOT the sum of `vehicles[].bookedMinutes`, and the difference is deliberate: a job
   * with two assignees on two vans is on BOTH vans' days, because both really do drive
   * there. Summing the vans would count that job's hours twice. This counts each visit
   * once, which is the right answer for "how much work is booked today" — and the wrong
   * one for "how long is each van out", which is what the per-van field is for.
   */
  plannedMinutes: number;
  /** Distinct calls today with no end time, counted once each. */
  stopsWithoutDuration: number;
  /** False when no geocoding key is configured — the map is empty for a reason. */
  geocodingOn: boolean;
  /**
   * False when no Routes key is configured — drive times are not merely absent, they are
   * switched OFF, and the page must not ask for them.
   *
   * ⭐ The sibling of `geocodingOn`, and it exists for the same reason at a higher price.
   * `routing.dayRoute` costs a billed Google call per van; a UI that calls it optimistically
   * and renders whatever comes back would bill N calls per page load to draw N copies of
   * "drive n/a". This flag is how the page knows not to ask at all. It reports what this
   * DEPLOYMENT was handed, never whether a given call succeeded — a key can be present and
   * a leg still unanswerable, which is what `DayRoute.degraded` is for.
   */
  routingOn: boolean;
}

/**
 * One van's day as a driven ROUTE (Sonar-experience 108, wired 2026-08-23).
 *
 * ⚠️ Keyed by `assetId` + `date`, NEVER by coordinates. The client naming points would make
 * this an org-crossing oracle — ask for any lat/lng and learn the drive time between two
 * places the caller cannot see — and it would let anyone bill Google arbitrarily. The server
 * resolves the van's stops from the same path `fleet.day` uses, so the route is always about
 * work the caller is already allowed to read.
 */
export const routingDayRouteInput = z
  .object({ assetId: z.string().uuid(), date: isoDate.optional() })
  .strict();
export type RoutingDayRouteInput = z.infer<typeof routingDayRouteInput>;
